import { Fab, Grid } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { NkSelect, _NkInput as NkInput } from '@nordkap/nordkap-ui';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { combineClasses } from '../../../utils/styling';
import { useStyles } from '../styles';
import {
  dropdownFieldValid,
  row,
  templateFieldSectionSize,
  templateMemberSize,
  textFieldValid,
} from '../utils';
import TemplateFieldMemberDelimiter from './templateFieldMemberDelimiter';
import { getFieldName } from './utils';

const TemplateFieldMember = props => {
  const { t } = useTranslation();
  const classes = useStyles();
  const templateContainer = props.usedContainer.useContainer();

  const [value, setValue] = useState('Unmapped');
  const [source, setSource] = useState('Unmapped');
  const [precedingDelimiter, setPrecedingDelimiter] = useState('Empty');
  const [valueOptions, setValueOptions] = useState([
    {
      value: 'Unmapped',
      title: <em>{t('positionTemplateRule.notSelected')}</em>,
    },
  ]);

  const [openValueSelect, setOpenValueSelect] = useState(false);
  const [openSourceSelect, setOpenSourceSelect] = useState(false);

  const customFieldValues = useMemo(
    () =>
      templateContainer.fieldsAndTemplates.customFields.map(cf => ({
        value: cf.customFieldId,
        title: cf.name,
      })),
    [templateContainer.fieldsAndTemplates.customFields]
  );

  const sourceOptions = useMemo(
    () => [
      {
        value: 'Unmapped',
        title: (
          <em className={classes.NotSelectedOption}>
            {t('positionTemplateRule.source')}
          </em>
        ),
      },
      {
        value: 'Position',
        title: t('positionTemplateRule.sourceCategories.position'),
      },
      {
        value: 'PositionFields',
        title: t('positionTemplateRule.sourceCategories.fields'),
      },
      {
        value: 'Event',
        title: t('positionTemplateRule.sourceCategories.event'),
      },
      {
        value: 'File',
        title: t('positionTemplateRule.sourceCategories.file'),
      },
      {
        value: 'Text',
        title: t('positionTemplateRule.sourceCategories.text'),
      },
    ],
    [classes.NotSelectedOption, t]
  );

  const changeValueOptions = useCallback(
    // eslint-disable-next-line consistent-return
    newSource => {
      const none = [
        {
          value: 'Unmapped',
          title: <em>{t('positionTemplateRule.notSelected')}</em>,
        },
      ];
      if (
        newSource &&
        (newSource === 'Position' ||
          newSource === 'Event' ||
          newSource === 'File')
      ) {
        const options = props.selectOptions[
          `${_.lowerFirst(newSource)}Options`
        ].map(option => ({
          value: option,
          title: t(`${_.lowerFirst(newSource)}.${option}`),
        }));
        setValueOptions(none.concat(options));
        return options;
      }
      if (newSource === 'PositionFields') {
        setValueOptions(none.concat(customFieldValues));
        return customFieldValues;
      }
    },
    [customFieldValues, props.selectOptions, t]
  );

  useEffect(() => {
    setSource(props.member.source);
    setValue(props.member.value);
    setPrecedingDelimiter(props.member.precedingDelimiter);
    changeValueOptions(props.member.source);
    setOpenSourceSelect(props.member.source === 'Unmapped');
  }, [changeValueOptions, setOpenSourceSelect, props.member]);

  const handleValueChange = useCallback(
    event => {
      setValue(event.target.value);
      templateContainer.changeValue(props, event.target.value);
    },
    [props, templateContainer]
  );

  const handleDelimiterChange = useCallback(
    newDelimiter => {
      templateContainer.changeDelimiter(props, newDelimiter);
    },
    [props, templateContainer]
  );

  const handleSourceChange = useCallback(
    event => {
      setSource(event.target.value);
      templateContainer.changeSource(props, event.target.value);

      if (event.target.value === 'Text') {
        setValue('');
        templateContainer.changeValue(props, '');
      } else {
        const values = changeValueOptions(event.target.value);
        if (values && values.length === 1) {
          handleValueChange({ target: { value: values[0].value } });
        } else {
          setValue('Unmapped');
          templateContainer.changeValue(props, 'Unmapped');
          setOpenValueSelect(event.target.value !== 'Unmapped');
        }
      }
    },
    [changeValueOptions, handleValueChange, props, templateContainer]
  );

  return (
    <>
      {props.memberIndex !== 0 && (
        <TemplateFieldMemberDelimiter
          delimiter={precedingDelimiter}
          changeDelimiter={handleDelimiterChange}
        />
      )}
      <Grid
        container
        item
        alignItems="flex-end"
        justifyContent="space-evenly"
        wrap="nowrap"
        direction="column"
        {...templateMemberSize}
        classes={{ root: classes.TemplateFieldMember }}
      >
        <Fab
          size="small"
          color="primary"
          aria-label="delete"
          name="deleteMember"
          onClick={() => props.removeMember(props.memberIndex)}
          classes={{ root: classes.DeleteMemberButton }}
        >
          <DeleteIcon />
        </Fab>
        <Grid container item xs={row}>
          {props.memberIndex === 0 ? (
            <label
              className={combineClasses([
                classes.Bold,
                classes.UpperCase,
                classes.FieldLabel,
              ])}
            >
              {getFieldName(props, t)}
            </label>
          ) : (
            <label
              className={combineClasses([
                classes.Bold,
                classes.UpperCase,
                classes.FieldLabel,
                classes.FollowingLabel,
              ])}
            >
              ...{getFieldName(props, t)}
            </label>
          )}
        </Grid>
        <Grid container item xs={row}>
          <Grid container item xs={row}>
            <Grid container item justifyContent="center" xs={row}>
              <Grid
                container
                item
                className={classes.Field}
                {...templateFieldSectionSize}
              >
                <NkSelect
                  variant="outlined"
                  name="source"
                  value={source}
                  onChange={handleSourceChange}
                  items={sourceOptions}
                  itemPropName="value"
                  itemTitlePropName="title"
                  wrapper={false}
                  required
                  isValid={source !== 'Unmapped'}
                  errorText={t('global.form.required')}
                  inputClasses={{ root: classes.FieldBackground }}
                  open={openSourceSelect}
                  onOpen={() => {
                    setOpenSourceSelect(true);
                  }}
                  onClose={() => {
                    setOpenSourceSelect(false);
                  }}
                />
              </Grid>
              <Grid
                container
                item
                {...templateFieldSectionSize}
                className={combineClasses([classes.Field, classes.ValueField])}
              >
                {source === 'Text' ? (
                  <NkInput
                    variant="outlined"
                    value={value}
                    onChange={handleValueChange}
                    placeholder={t('text.value')}
                    name={t('positionTemplateRule.value')}
                    errorText={t('global.form.required')}
                    isValid={textFieldValid(value)}
                    inputClasses={{ root: classes.FieldBackground }}
                    required
                  />
                ) : (
                  <NkSelect
                    variant="outlined"
                    name="value"
                    value={value}
                    onChange={handleValueChange}
                    items={valueOptions}
                    itemPropName="value"
                    itemTitlePropName="title"
                    isValid={dropdownFieldValid(value)}
                    errorText={t('global.form.required')}
                    required
                    disabled={source === 'Unmapped'}
                    wrapper={false}
                    inputClasses={{ root: classes.FieldBackground }}
                    open={openValueSelect}
                    onOpen={() => {
                      setOpenValueSelect(true);
                    }}
                    onClose={() => {
                      setOpenValueSelect(false);
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

TemplateFieldMember.propTypes = {
  member: PropTypes.object,
  selectOptions: PropTypes.object,
  memberIndex: PropTypes.number,
  rule: PropTypes.object,
  fieldName: PropTypes.string,
  transactionType: PropTypes.string,
  objectIndex: PropTypes.number,
  hover: PropTypes.bool,
  removeMember: PropTypes.func,
  usedContainer: PropTypes.object,
};

export default TemplateFieldMember;
